import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { CommonModule as AngularCommonModule } from '@angular/common';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { appRoutes } from './app.routes';
import { DashboardDataCacheService } from './common/rest/dashboard.data.cache.service';
import { VersionService } from './common/versioning/version.service';
import { GoogleLoginCfgService } from './auth/google/googleLoginCfg.service';
import { ServerAddressesCfgService } from './util/serverAddressesCfg.service';
import { AccessManagerCfgService } from './auth/accessManager/accessManagerCfg.service';
import { FeaturePermissionsCfgService } from './auth/raas/featurePermissionsCfg.service';
import { SigV4CfgService } from './auth/aws/sigv4Cfg.service';
import { AppLinkNavigatorService } from './common/appLinks/appLinkNavigator.service';
import { OfficeSelectorService } from './common/officeSelector/officeSelector.service';
import { HeaderCfgService } from './common/header/headerCfg.service';
import { LoggerService } from './common/logging/logger.service';
import { LauncherCfgService } from './common/launcher/launcherCfg.service';
import { SmsService } from './common/sms/sms.service';
import { SmsTemplateRepositoryService } from './common/sms/smsTemplateRepository.service';
import { NotificationHubService } from './common/notificationHub/notificationHub.service';
import { CommonCfgService } from './common/rest/common-cfg.service';
import { ErrorHandler } from '@angular/core';
import { ErrorHandlerService } from './common/errorHandling/errorHandler.service';
import { DynamicComponentFactoryBuilderService } from './util/dynamicComponentFactoryBuilder.service';
import { initAppModule } from './util/initApp';
import { APP_INITIALIZER } from '@angular/core';
import { AppLinksCfgService } from './common/appLinks/appLinksCfg.service';
import { AppLinksProfessionalsCfgService } from './common/appLinks/appLinksProfessionalsCfg.service';
import { OfficeSelectorCfgService } from './common/officeSelector/officeSelectorCfg.service';
import { HelpCfgService } from './common/help/helpCfg.service';
import { LoggerCfgService } from './common/logging/loggerCfg.service';
import { SmsCfgService } from './common/sms/smsCfg.service';
import { NotificationHubCfgService } from './common/notificationHub/notificationHubCfg.service';
import { AbsencesKpiCfgService } from './dashboard/kpi/services/absencesKpi/absencesKpiCfg.service';
import { DashboardCfgService } from './dashboard/dashboardCfg.service';
import { AdministrativeManagementDigitalizationKpiCfgService } from './dashboard/kpi/services/administrativeManagementDigitalizationKpi/administrativeManagementDigitalizationKpiCfg.service';
import { BucketStatusKpiCfgService } from './dashboard/kpi/services/bucketStatusKpi/bucketsStatusKpiCfg.service';
import { ContractsKpiCfgService } from './dashboard/kpi/services/contractsKpi/contractsKpiCfg.service';
import { OccupationalRiskPreventionKpiCfgService } from './dashboard/kpi/services/occupationalRiskPreventionKpi/occupationalRiskPreventionKpiCfg.service';
import { OffersInNegotiationKpiCfgService } from './dashboard/kpi/services/offersInNegotiationKpi/offersInNegotiationKpiCfg.service';
import { OrdersKpiCfgService } from './dashboard/kpi/services/ordersKpi/ordersKpiCfg.service';
import { VisitsCallsTargetsKpiCfgService } from './dashboard/kpi/services/visitsCallsTargetsKpi/visitsCallsTargetsKpiCfg.service';
import { MonthlyTargetsKpiCfgService } from './dashboard/kpi/services/monthlyTargetsKpi/monthlyTargetsKpiCfg.service';
import { DiscontinuousPermanentWorkersKpiCfgService } from './dashboard/kpi/services/discontinuousPermanentWorkersKpi/discontinuousPermanentWorkersKpiCfg.service';
import { WorkersAppealKpiCfgService } from './dashboard/kpi/services/workersAppealKpi/workersAppealKpiCfg.service';
import { PendingOrdersFDKpiCfgService } from './dashboard/kpi/services/pendingOrdersFDKpi/pendingOrdersFDKpiCfg.service';
import { VacanciesKpiCfgService } from './vacancies/services/vacanciesKpiCfg.service';
import { ContractsDirectKpiCfgService } from './dashboardDirect/kpi/services/contractsKpi/contractsDirectKpiCfg.service';
import { OccupationalRiskPreventionDirectKpiCfgService } from './dashboardDirect/kpi/services/occupationalRiskPreventionKpi/occupationalRiskPreventionDirectKpiCfg.service';
import { AbsencesDirectKpiCfgService } from './dashboardDirect/kpi/services/absencesKpi/absencesDirectKpiCfg.service';
import { DashboardRecruitmentCfgService } from './dashboard-recruitment/services/dashboard-recruitment-cfg.service';
import { NoveltyCfgService } from './novelties/services/novelty-cfg.service';
import { CommonModule } from './common/common.module';
import { UtilModule } from './util/util.module';
import { LoginModule } from './login/login.module';
import { DashboardCommonModule } from './dashboardCommon/dashboardCommon.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { DashboardDirectModule } from './dashboardDirect/dashboardDirect.module';
import { DashboardRecruitmentModule } from './dashboard-recruitment/dashboard-recruitment.module';
import { HomeClassicModule } from './homeClassic/homeClassic.module';
import { GoalsModule } from './goals/goals.module';
import { NoveltiesModule } from './novelties/novelties.module';
import { VacanciesModule } from './vacancies/vacancies.module';
import { PowerBiCfgService } from './common/launcher/powerbi/services/powerbi-cfg.service';
import { ShowPowerBiCfgService } from './common/launcher/powerbi/services/showPowerbi-cfg.service';
import { ImpersonationService } from './auth/apigee/impersonation.service';
import { ImpersonationCfgService } from './auth/apigee/impersonationCfg.service';
import { ApigeeCfgService } from './auth/apigee/apigee-cfg.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: APP_INITIALIZER,
      useFactory: initAppModule,
      deps: [
        ServerAddressesCfgService, VersionService, DynamicComponentFactoryBuilderService,
        GoogleLoginCfgService, ApigeeCfgService, ImpersonationCfgService, AccessManagerCfgService,
        ImpersonationService, FeaturePermissionsCfgService, SigV4CfgService, AppLinksCfgService,
        AppLinksProfessionalsCfgService, OfficeSelectorCfgService, HeaderCfgService, HelpCfgService,
        LoggerCfgService, LauncherCfgService, SmsCfgService, NotificationHubCfgService,
        AbsencesKpiCfgService, DashboardCfgService, AdministrativeManagementDigitalizationKpiCfgService,
        BucketStatusKpiCfgService, ContractsKpiCfgService, OccupationalRiskPreventionKpiCfgService,
        OffersInNegotiationKpiCfgService, OrdersKpiCfgService, VisitsCallsTargetsKpiCfgService,
        MonthlyTargetsKpiCfgService, DiscontinuousPermanentWorkersKpiCfgService, WorkersAppealKpiCfgService,
        PendingOrdersFDKpiCfgService, VacanciesKpiCfgService, ContractsDirectKpiCfgService,
        OccupationalRiskPreventionDirectKpiCfgService, AbsencesDirectKpiCfgService,
        DashboardRecruitmentCfgService, CommonCfgService, NoveltyCfgService, PowerBiCfgService, ShowPowerBiCfgService
      ],
      multi: true
    },
    DashboardDataCacheService,
    VersionService,
    GoogleLoginCfgService,
    ApigeeCfgService,
    ServerAddressesCfgService,
    ImpersonationCfgService,
    AccessManagerCfgService,
    ImpersonationService,
    FeaturePermissionsCfgService,
    SigV4CfgService,
    AppLinkNavigatorService,
    OfficeSelectorService,
    HeaderCfgService,
    LoggerService,
    LauncherCfgService,
    SmsService,
    SmsTemplateRepositoryService,
    NotificationHubService,
    CommonCfgService,
    DynamicComponentFactoryBuilderService,
    importProvidersFrom(
      AngularCommonModule,
      NgbModule,
      CommonModule,
      UtilModule,
      LoginModule,
      DashboardCommonModule,
      DashboardModule,
      DashboardDirectModule,
      DashboardRecruitmentModule,
      HomeClassicModule,
      GoalsModule,
      NoveltiesModule,
      VacanciesModule
    )
  ]
};
